import { fetchAcousticSnapshotsFromServer, fetchAcousticThresholdsFromServer } from '../acousticCalibrationApi';
import { readCalibrationStore, replaceCalibrationStore } from './calibrationStorage';
import type { AcousticCalibrationStore } from './calibrationTypes';
import {
  readThresholdOverrideStore,
  replaceThresholdOverrideStore,
  type AcousticThresholdOverrideStore
} from './acousticThresholdOverrides';
let hydratePromise: Promise<void> | null = null;
let lastHydratedAt = 0;
const HYDRATE_COOLDOWN_MS = 5000;

function isAdminUser(user: { role?: string; allRoles?: string[]; roles?: string[] } | null): boolean {
  if (!user) return false;
  const roles = new Set<string>();
  if (user.role) roles.add(String(user.role).toLowerCase());
  for (const role of user.allRoles ?? user.roles ?? []) {
    roles.add(String(role).toLowerCase());
  }
  return roles.has('admin');
}

function isStudentUser(user: { role?: string } | null): boolean {
  return String(user?.role ?? '').toLowerCase() === 'student';
}

export async function hydrateAcousticCalibrationFromServer(
  user: { role?: string; allRoles?: string[]; roles?: string[] } | null
): Promise<void> {
  if (!user || isStudentUser(user)) return;

  const now = Date.now();
  if (hydratePromise) return hydratePromise;
  if (now - lastHydratedAt < HYDRATE_COOLDOWN_MS) return;

  hydratePromise = (async () => {
    const thresholds = await fetchAcousticThresholdsFromServer();
    if (thresholds) {
      replaceThresholdOverrideStore(thresholds, { persistRemote: false });
    }

    if (isAdminUser(user)) {
      const snapshotStore = await fetchAcousticSnapshotsFromServer();
      if (snapshotStore?.snapshots) {
        replaceCalibrationStore(snapshotStore, { persistRemote: false });
      }
    }

    lastHydratedAt = Date.now();
  })().finally(() => {
    hydratePromise = null;
  });

  return hydratePromise;
}

export function getLocalCalibrationState(): {
  thresholds: AcousticThresholdOverrideStore;
  snapshots: AcousticCalibrationStore;
} {
  return {
    thresholds: readThresholdOverrideStore(),
    snapshots: readCalibrationStore()
  };
}
